import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Check, Copy, Loader2, RefreshCw, ShieldCheck, Store, X } from 'lucide-react';
import api from '../../lib/api';

const CHECK_COPY = {
  account_exists: 'Reviewer account exists',
  password_logs_in: 'Password signs in (no OTP)',
  active: 'Active, not suspended',
  phone_e164: 'Phone stored correctly',
  phone_verified: 'No OTP gate after login',
  paid_tier: 'Paid Elite tier (not a trial)',
  otp_fallback_configured: 'Fixed OTP for the reviewer phone',
  membership_not_blocked: 'Membership not blocked',
  access_window_future: 'Access window in the future',
  soulai_coach_full_access: 'SoulAI Coach unlocked',
  baby_tracker_full_access: 'Baby Tracker unlocked',
  prenatal_stage: 'Prenatal journey',
  edd_in_future: 'EDD in the future',
  india_pricing: 'INR pricing (stable)',
};

const CopyRow = ({ label, value, testid }) => {
  const [done, setDone] = useState(false);
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <code className="rounded bg-muted px-2 py-1 text-xs" data-testid={`${testid}-value`}>{value}</code>
      <Button size="sm" variant="ghost" data-testid={`${testid}-copy`}
              onClick={() => { navigator.clipboard?.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); }}>
        {done ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
};

export const StoreReviewAccountCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prepared, setPrepared] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.get('/api/admin/ops/reviewer-readiness');
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not read reviewer readiness');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const prepare = async () => {
    setBusy(true); setError(null); setPrepared(null);
    try {
      const r = await api.post('/api/admin/ops/seed-reviewer-account');
      setData(r.data); setPrepared(r.data.prepared || {});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Preparation failed');
    } finally { setBusy(false); }
  };

  const checks = Object.entries(data?.checks || {});
  const passed = checks.filter(([, v]) => v.ok).length;

  return (
    <div className="rounded-2xl border p-4 space-y-4" data-testid="store-review-account-card">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Store className="h-4 w-4 text-primary" />Store review sign-in account
        </h3>
        {data && (
          <Badge data-testid="reviewer-ready-pill"
                 className={data.ready ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}>
            {data.ready ? `Ready · ${passed}/${checks.length} checks` : `Not ready · ${data.blockers?.length || '?'} to fix`}
          </Badge>
        )}
        <Button size="sm" variant="outline" className="ml-auto" onClick={load} disabled={busy}
                data-testid="reviewer-refresh-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          <span className="ml-1">Re-check</span>
        </Button>
        <Button size="sm" onClick={prepare} disabled={busy} data-testid="reviewer-prepare-btn">
          <ShieldCheck className="h-3.5 w-3.5" />
          <span className="ml-1">Prepare reviewer account</span>
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Google Play and Apple reviewers cannot create accounts, cannot receive an OTP and cannot use
        trials. This prepares the demo learner with real paid access, a bookable review-only batch and a
        ready SoulAI Coach plan. Safe to press any number of times — it repairs whatever has drifted.
      </p>

      {error && <div className="rounded-lg bg-rose-50 p-2 text-sm text-rose-700" data-testid="reviewer-error">{error}</div>}

      {data && (
        <div className="space-y-2">
          <CopyRow label="Email" value={data.email} testid="reviewer-email" />
          {data.password && <CopyRow label="Password" value={data.password} testid="reviewer-password" />}
          <CopyRow label="Phone" value={data.phone} testid="reviewer-phone" />
        </div>
      )}

      {prepared && (
        <div className="rounded-lg bg-emerald-50 p-2 text-xs text-emerald-800 space-y-1" data-testid="reviewer-prepared">
          {Object.entries(prepared).map(([k, v]) => <div key={k}><b>{k.replace(/_/g, ' ')}:</b> {String(v)}</div>)}
        </div>
      )}

      {checks.length > 0 && (
        <div className="grid gap-1 sm:grid-cols-2" data-testid="reviewer-checks">
          {checks.map(([key, v]) => (
            <div key={key} className="flex items-start gap-2 text-xs" data-testid={`reviewer-check-${key}`}>
              {v.ok ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-600" />}
              <span>
                <span className={v.ok ? '' : 'font-semibold text-rose-700'}>{CHECK_COPY[key] || key}</span>
                <span className="text-muted-foreground"> — {v.detail}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StoreReviewAccountCard;
